import { Icon, MenuItem, Tooltip } from "@material-ui/core";
import React from "react";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserRole,
} from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { Icon as BIcon } from "js/Components/Icon";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import {
    ShowDialog,
    ShowDialogAsync,
    ShowErrorDialog,
    ShowInputDialog,
    ShowWarningDialog,
} from "js/react/components/Dialogs/BaseDialog";
import { DeleteWorkspaceResourceFolderDialog } from "js/react/components/Dialogs/DeleteWorkspaceResourceFolderDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import GuestWorkspaceDialog from "js/react/components/Dialogs/GuestWorkspaceDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import { IconMenu } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";

const logger = getLogger();

const RestrictedMenuItem = styled(MenuItem)`
    &&& {
        color: ${themeColors.lighterGray};
    }
`;

const CapitalizedLabel = styled.span`
    text-transform: capitalize;
`;

const HeaderActionIconMenu = styled(IconMenu).attrs({
    size: "small",
    fontSize: "small",
})`
    &&& {
        padding: 0px;
    }
`;

const HeaderFolderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 10px;
`;

const HeaderActionPill = styled.div`
    &&& {
        background-color: #E9E9E9;
        display: flex;
        border-radius: 50%;
        align-items: center;
        justify-content: center;
        padding: 4px;

        .icon,
        .bai-icon {
            display: flex;
            height: 16px;
            font-size: 16px;
            line-height: 1;
            color: #777;
            align-items: center;
        }

        .icon:hover,
        .bai-icon:hover {
            color: #555 !important;
        }

        &:empty {
            display: none;
        }
    }
`;

export const CREATE_FOLDER_CONFIG: Partial<Record<WorkspaceResourceType, { inputId: string; logTag: string }>> = {
    [WorkspaceResourceType.SLIDE]: {
        inputId: "create_slide_folder_input",
        logTag: "WorkspaceResourceFolderMenu",
    },
    [WorkspaceResourceType.SLIDE_TEMPLATE]: {
        inputId: "create_slide_template_folder_input",
        logTag: "WorkspaceResourceFolderMenu",
    },
    [WorkspaceResourceType.ASSET]: {
        inputId: "create_asset_folder_input",
        logTag: "WorkspaceResourceFolderMenu",
    },
    [WorkspaceResourceType.PRESENTATION_TEMPLATE]: {
        inputId: "create_presentation_template_folder_input",
        logTag: "WorkspaceResourceFolderMenu",
    },
};

const folderLabel = (isSubFolder: boolean) => isSubFolder ? "subfolder" : "folder";

export function isWorkspaceFolderNameTaken(
    name: string,
    folders: IWorkspaceResourceFolder[],
    resourceTypes: WorkspaceResourceType[],
    { parentFolderId, excludeFolderId }: { parentFolderId?: string | null; excludeFolderId?: string }
) {
    return folders
        .filter(f => {
            if (!resourceTypes.includes(f.resourcesType)) {
                return false;
            }
            if (excludeFolderId && f.id === excludeFolderId) {
                return false;
            }
            if (parentFolderId) {
                return f.parentFolderId === parentFolderId;
            }
            return !f.parentFolderId;
        })
        .some(f => f.name === name);
}

export async function promptAndCreateWorkspaceResourceFolder({
    parentFolder,
    resourceTypes,
    resourceFolders,
    folderType,
    createResourcesType,
    shareCreatedFoldersWithDefaultGroups = true,
    onCreated,
}: {
    parentFolder?: IWorkspaceResourceFolder;
    resourceTypes: WorkspaceResourceType[];
    resourceFolders: IWorkspaceResourceFolder[];
    folderType?: WorkspaceResourceFolderType;
    createResourcesType?: WorkspaceResourceType;
    shareCreatedFoldersWithDefaultGroups?: boolean;
    onCreated?: (folder: IWorkspaceResourceFolder) => void;
}): Promise<IWorkspaceResourceFolder | undefined> {
    const isSubFolder = Boolean(parentFolder);
    const canCreateFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const resourcesTypeForCreate = createResourcesType ?? resourceTypes[0];
    const createConfig = resourcesTypeForCreate ? CREATE_FOLDER_CONFIG[resourcesTypeForCreate] : undefined;

    if (!canCreateFolders) {
        if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE)) {
            ShowDialog(ChangePlanDialog, {
                featuredPlan: WorkspacePlanTier.TEAM,
            });
        } else {
            ShowWarningDialog({
                title: t("Oops!"),
                message: t("You do not have permission to create folders!"),
            });
        }
        return;
    }

    const folderName = await new Promise<string | undefined>(resolve => ShowDialog(InputConfirmationDialog, {
        title: t(isSubFolder ? "Create Subfolder" : "Create Folder"),
        input: {
            type: "text",
            placeholder: t("Type folder name..."),
            id: createConfig?.inputId,
            inputProps: {
                maxLength: 50,
            },
        },
        acceptCallback: (input: string) => resolve(input.trim()),
    }));

    if (!folderName) {
        return;
    }

    if (isWorkspaceFolderNameTaken(folderName, resourceFolders, resourceTypes, { parentFolderId: parentFolder?.id ?? null })) {
        ShowWarningDialog({
            title: t(`Unable to create ${folderLabel(isSubFolder)}`),
            message: t(`Please enter a unique ${folderLabel(isSubFolder)} name.`),
        });
        return;
    }

    const resourcesType = parentFolder?.resourcesType ?? resourcesTypeForCreate;
    const folderTypeToCreate = parentFolder?.folderType ?? folderType;

    if (!resourcesType || !folderTypeToCreate) {
        return;
    }

    try {
        const folder = await withProgressDialog({
            title: t(`Creating ${folderLabel(isSubFolder)}...`),
            action: () => {
                const createRequest = {
                    name: folderName,
                    resourcesType,
                    folderType: folderTypeToCreate,
                    ...(parentFolder ? { parentFolderId: parentFolder.id } : {}),
                };

                if (!parentFolder && shareCreatedFoldersWithDefaultGroups) {
                    return WorkspaceController.createWorkspaceResourceFolderAndShareWithDefaultGroups(createRequest);
                }

                return WorkspaceController.createWorkspaceResourceFolder(createRequest);
            }
        });

        onCreated?.(folder);
        return folder;
    } catch (err) {
        logger.error(err, `[${createConfig?.logTag ?? "WorkspaceResourceFolderMenu"}] promptAndCreateWorkspaceResourceFolder failed`);

        ShowErrorDialog({
            title: t(`Unable to create ${folderLabel(isSubFolder)}`),
            message: (err as Error).message,
        });
    }
}

export type WorkspaceResourceFolderMenuItemsProps = {
    folder: IWorkspaceResourceFolder;
    resourceTypes: WorkspaceResourceType[];
    resourceFolders: IWorkspaceResourceFolder[];
    selectedFolderId?: string | null;
    onSelectFolder?: (folderId: string | null) => void;
    onCreateFolder?: (parentFolder?: IWorkspaceResourceFolder) => void | Promise<void>;
};

export function WorkspaceResourceFolderMenuItems({
    folder,
    resourceTypes,
    resourceFolders,
    selectedFolderId = null,
    onSelectFolder,
    onCreateFolder,
}: WorkspaceResourceFolderMenuItemsProps) {
    const isSubFolder = Boolean(folder.parentFolderId);

    const canRenameFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToRenameFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.RESOURCE_FOLDERS,
        PermissionActionType.CREATE
    );
    const canDeleteFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
    const canUpgradeToDeleteFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.RESOURCE_FOLDERS,
        PermissionActionType.CREATE
    );

    const handleCanUpgradeAction = (cta: string) => () => {
        if (WorkspaceController.role === WorkspaceUserRole.GUEST) {
            ShowDialog(GuestWorkspaceDialog);
        } else {
            ShowDialog(ChangePlanDialog, { featuredPlan: WorkspacePlanTier.PRO, analytics: { cta } });
        }
    };

    const isFolderNameTaken = (name: string) => isWorkspaceFolderNameTaken(name, resourceFolders, resourceTypes, {
        parentFolderId: folder.parentFolderId,
        excludeFolderId: folder.id,
    });

    const handleRenameFolder = async () => {
        let name = await ShowInputDialog({
            title: t(isSubFolder ? "Rename Subfolder" : "Rename Folder"),
            maxChars: 50,
            value: folder.name,
        });

        if (!name) {
            return;
        }

        name = name.trim();

        if (folder.name === name) {
            return;
        }

        try {
            if (isFolderNameTaken(name)) {
                throw new Error(t(`Please enter a unique ${folderLabel(isSubFolder)} name.`));
            }

            await withProgressDialog({
                title: t(`Renaming ${folderLabel(isSubFolder)}...`),
                action: () => WorkspaceController.updateWorkspaceResourceFolder({ folderId: folder.id, name }),
            });
        } catch (err) {
            logger.error(err, "[WorkspaceResourceFolderMenu] handleRenameFolder failed");

            ShowErrorDialog({
                title: t(`Unable to rename ${folderLabel(isSubFolder)}`),
                message: (err as Error).message,
            });
        }
    };

    const handleDeleteFolder = async () => {
        const deleted = await ShowDialogAsync(DeleteWorkspaceResourceFolderDialog, {
            folder,
            resourceFolders,
            resourceTypes,
        });

        if (!deleted || folder.id !== selectedFolderId) {
            return;
        }

        onSelectFolder?.(folder.parentFolderId ?? null);
    };

    return (
        <>
            {canRenameFolders &&
                <MenuItem onClick={handleRenameFolder}>
                    <Icon>label</Icon>
                    <CapitalizedLabel>{t(`Rename ${folderLabel(isSubFolder)}`)}</CapitalizedLabel>
                </MenuItem>}
            {canUpgradeToRenameFolders && (
                <RestrictedMenuItem divider onClick={handleCanUpgradeAction("rename_folder")}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    <CapitalizedLabel>{t(`Rename ${folderLabel(isSubFolder)}`)}</CapitalizedLabel>
                </RestrictedMenuItem>
            )}

            {!isSubFolder && canRenameFolders && (
                <MenuItem onClick={() => onCreateFolder?.(folder)}>
                    <Icon>folder_open</Icon>
                    <CapitalizedLabel>{t("Create Subfolder")}</CapitalizedLabel>
                </MenuItem>)}

            <hr />
            {canDeleteFolders &&
                <MenuItem onClick={handleDeleteFolder}>
                    <Icon>delete</Icon>
                    <CapitalizedLabel>{t(`Delete ${folderLabel(isSubFolder)}`)}</CapitalizedLabel>
                </MenuItem>}

            {canUpgradeToDeleteFolders &&
                <RestrictedMenuItem onClick={handleCanUpgradeAction("delete_folder")}>
                    <UpgradeIcon style={{ marginRight: 8 }} />
                    <CapitalizedLabel>{t(`Delete ${folderLabel(isSubFolder)}`)}</CapitalizedLabel>
                </RestrictedMenuItem>}
        </>
    );
}

export type WorkspaceResourceFolderHeaderActionsProps = {
    folderId: string;
    resourceTypes: WorkspaceResourceType[];
    resourceFolders: IWorkspaceResourceFolder[];
    folderType?: WorkspaceResourceFolderType;
    createResourcesType?: WorkspaceResourceType;
    onSelectFolder?: (folderId: string | null) => void;
    onCreateItem?: () => void;
    showCreateButton?: boolean;
};

export function WorkspaceResourceFolderHeaderActions({
    folderId,
    resourceTypes,
    resourceFolders,
    folderType = WorkspaceResourceFolderType.SHARED,
    createResourcesType,
    onSelectFolder,
    onCreateItem,
    showCreateButton = false,
}: WorkspaceResourceFolderHeaderActionsProps) {
    if (app.isConstrained) {
        return null;
    }

    const folder = resourceFolders.find(f => f.id === folderId);
    if (!folder) {
        return null;
    }

    const handleCreateSubfolder = () => promptAndCreateWorkspaceResourceFolder({
        parentFolder: folder,
        resourceTypes,
        resourceFolders,
        folderType,
        createResourcesType,
        shareCreatedFoldersWithDefaultGroups: false,
        onCreated: created => onSelectFolder?.(created.id),
    });

    return (
        <HeaderFolderActions data-testid="workspace-resource-folder-header-actions">
            <HeaderActionPill>
                <Tooltip title={t("Manage folder")}>
                    <HeaderActionIconMenu>
                        <WorkspaceResourceFolderMenuItems
                            folder={folder}
                            resourceTypes={resourceTypes}
                            resourceFolders={resourceFolders}
                            selectedFolderId={folderId}
                            onSelectFolder={onSelectFolder}
                            onCreateFolder={handleCreateSubfolder}
                        />
                    </HeaderActionIconMenu>
                </Tooltip>
            </HeaderActionPill>
            {showCreateButton && onCreateItem && (
                <Tooltip title={t("Create new item in folder")}>
                    <HeaderActionPill data-testid="create-resource-in-folder-button">
                        <BIcon
                            size={16}
                            onClick={event => {
                                event.stopPropagation();
                                onCreateItem();
                            }}
                        >
                            add
                        </BIcon>
                    </HeaderActionPill>
                </Tooltip>
            )}
        </HeaderFolderActions>
    );
}
